import {
	type BreakpointKey,
	breakpoint as getBreakpoint
} from "@/app/utilities/breakpoint";
import type { AspectRatioKey } from "@/app/utilities/types";

import type {
	Asset,
	AssetBlock,
	ImageAsset,
	VideoAsset
} from "@/types/references";

interface ImageSize {
	height: number;
	width: number;
}

type ImageByBreakpoint = Partial<Record<BreakpointKey, ImageSize>>;

interface ImageRenditionProps extends Partial<ImageSize> {
	loader: (input: LoaderInput) => string;
	src: string;
	alt: string;
	sizes?: string;
}

interface LoaderInput {
	src: string;
	width: number;
	quality?: number;
}

/**
 * Image sizes, by aspect ratio and breakpoint
 */
const IMAGE_SIZES: Partial<Record<AspectRatioKey, ImageByBreakpoint>> = {
	"1:1": {
		xs: {
			height: 200,
			width: 200
		},
		xl: {
			height: 400,
			width: 400
		}
	},
	"2:3": {
		xs: {
			height: 630,
			width: 420
		},
		xl: {
			height: 492,
			width: 328
		}
	},
	"16:9": {
		xs: {
			height: 200,
			width: 340
		},
		xl: {
			height: 480,
			width: 820
		}
	}
};

/**
 * Generate sizes property for images
 * @param src Image src
 * @param images Object with image sizes by breakpoint
 */
function getImageSizes(images: ImageByBreakpoint): string {
	const stringArr = [
		`(max-width: ${getBreakpoint("xl")}) ${images.xs?.width}px`,
		`${images.xl?.width}px`
	];

	return stringArr.join(", ");
}

/**
 * Next.JS image loader to generate renditions for the currently used DAM.
 */
export function imageLoader({ src, width, quality }: LoaderInput): string {
	const url = new URL(src);

	url.searchParams.set("w", width.toString());
	url.searchParams.set("quality", (quality || 75).toString());
	url.searchParams.delete("crop");
	url.searchParams.delete("keep");
	url.searchParams.delete("h");

	return url.href;
}

type ImageReference = ImageAsset | AssetBlock | Asset | VideoAsset;

/**
 * Image type guard
 */
function isImageAsset(image?: ImageReference): image is ImageAsset {
	return !!(image as ImageAsset)?.image;
}

/**
 * Asset Block type guard
 */
function isAssetBlock(image?: ImageReference): image is AssetBlock {
	return !!(image as AssetBlock)?.blockType;
}

function getImageReferenceSrc(image?: ImageReference): string {
	if (!image) return "";

	const safeUrl = (value?: string | null) => value || undefined;

	const isVideo =
		(isAssetBlock(image) && image.blockType === "video_asset") ||
		("video" in image && "poster_image" in image);

	if (isVideo) {
		const v = image as VideoAsset;
		return (
			safeUrl(v.custom?.[0]?.url) ||
			safeUrl(v.custom?.[0]?.thumbnailUrl) ||
			safeUrl(v.acquia?.[0]?.url) ||
			safeUrl(v.acquia?.[0]?.thumbnailUrl) ||
			safeUrl(v.image_field?.[0]?.url) ||
			safeUrl(v.image_field?.[0]?.thumbnailUrl) ||
			safeUrl(v.poster_image) ||
			""
		);
	}

	if (isImageAsset(image)) {
		return (
			safeUrl(image.custom?.[0]?.url) ||
			safeUrl(image.custom?.[0]?.thumbnailUrl) ||
			safeUrl(image.acquia?.[0]?.url) ||
			safeUrl(image.acquia?.[0]?.thumbnailUrl) ||
			safeUrl(image.image_field?.[0]?.url) ||
			safeUrl(image.image_field?.[0]?.thumbnailUrl) ||
			safeUrl(image.image) ||
			""
		);
	}

	if (isAssetBlock(image)) {
		if ("poster_image" in image) {
			return (
				safeUrl(image.poster_image) ||
				safeUrl(image.custom?.[0]?.url) ||
				safeUrl(image.custom?.[0]?.thumbnailUrl) ||
				safeUrl(image.acquia?.[0]?.url) ||
				safeUrl(image.acquia?.[0]?.thumbnailUrl) ||
				safeUrl(image.image_field?.[0]?.url) ||
				safeUrl(image.image_field?.[0]?.thumbnailUrl) ||
				""
			);
		}

		return (
			safeUrl(image.custom?.[0]?.url) ||
			safeUrl(image.custom?.[0]?.thumbnailUrl) ||
			safeUrl(image.acquia?.[0]?.url) ||
			safeUrl(image.acquia?.[0]?.thumbnailUrl) ||
			safeUrl(image.image_field?.[0]?.url) ||
			safeUrl(image.image_field?.[0]?.thumbnailUrl) ||
			""
		);
	}

	return safeUrl(image.image_url) || "";
}

function getImageReferenceAlt(image?: ImageReference): string {
	if (!image) return "";
	if (isAssetBlock(image) && image.blockType === "video_asset") {
		return image.poster_image_alt_text || "";
	}
	if ("video" in image && "poster_image_alt_text" in image) {
		return (image as VideoAsset).poster_image_alt_text || "";
	}
	return image.image_alt_text || "";
}
export function normalizeImageAsset(
	asset?: ImageAsset | null,
	fallbackAlt?: string
): { url: string; alt: string } | undefined {
	if (!asset) return undefined;

	const url =
		firstMediaUrl(asset.custom) ??
		firstMediaUrl(asset.acquia) ??
		firstMediaUrl(asset.image_field) ??
		nonEmptyString(asset.image);

	if (!url) return undefined;

	const alt = nonEmptyString(asset.image_alt_text) ?? fallbackAlt ?? "";
	return { url, alt };
}

function firstMediaUrl(
	list: Array<{ url?: string | null; thumbnailUrl?: string }> | undefined
): string | undefined {
	if (!list?.length) return undefined;
	return nonEmptyString(list[0].url) ?? nonEmptyString(list[0].thumbnailUrl);
}

function nonEmptyString(value: string | null | undefined): string | undefined {
	return value && value.trim() !== "" ? value : undefined;
}

/**
 * Determines image rendition properties, based on expected aspect ratio and viewport
 */
export function resolveImageProps(
	aspectRatio?: AspectRatioKey,
	image?: ImageReference,
	breakpoint: BreakpointKey = "xxl"
): ImageRenditionProps {
	const imageSrc = getImageReferenceSrc(image);
	const imageAlt = getImageReferenceAlt(image);

	const response = { loader: imageLoader, src: imageSrc, alt: imageAlt };

	if (!aspectRatio) {
		return response;
	}

	const imageSizeByRatio = IMAGE_SIZES?.[aspectRatio];

	if (!imageSizeByRatio) {
		return response;
	}

	const isDesktop = ["xxl", "xl"].includes(breakpoint as string);
	const imageSize = (
		isDesktop ? imageSizeByRatio["xl"] : imageSizeByRatio["xs"]
	) as ImageSize;

	return {
		...response,
		...imageSize,
		sizes: getImageSizes(imageSizeByRatio)
	};
}
